import React, { useEffect, useState, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../../config';

const ACTIONS = [
    { key: 'view', label: 'Просмотр' },
    { key: 'create', label: 'Создание' },
    { key: 'edit', label: 'Редактирование' },
    { key: 'delete', label: 'Удаление' },
];

const RolesPage = ({ onShowToast }) => {
    const [sections, setSections] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [draftPerms, setDraftPerms] = useState({});
    const [createName, setCreateName] = useState('');
    const [creating, setCreating] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [s, r] = await Promise.all([
                fetch(`${API_BASE_URL}/api/roles/sections`, { credentials: 'include' }),
                fetch(`${API_BASE_URL}/api/roles`, { credentials: 'include' }),
            ]);
            if (s.ok) {
                const d = await s.json();
                setSections(d.sections || []);
            }
            if (r.ok) {
                const d = await r.json();
                setItems(d.items || []);
            }
        } catch (e) {
            onShowToastRef.current?.(e.message || 'Ошибка загрузки');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const openEdit = (row) => {
        if (row.name === 'admin' && row.is_system) {
            onShowToast && onShowToast('Права системного администратора не редактируются');
            return;
        }
        setEditing(row);
        setDraftPerms(JSON.parse(JSON.stringify(row.permissions || {})));
    };

    const togglePerm = (sectionKey, actionKey) => {
        setDraftPerms((prev) => ({
            ...prev,
            [sectionKey]: {
                ...(prev[sectionKey] || {}),
                [actionKey]: !(prev[sectionKey] || {})[actionKey],
            },
        }));
    };

    const saveEdit = async () => {
        if (!editing) return;
        try {
            const r = await fetch(`${API_BASE_URL}/api/roles/${editing.id}`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ permissions: draftPerms }),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(data.detail || 'Ошибка сохранения');
            onShowToast && onShowToast('Роль обновлена');
            setEditing(null);
            await load();
        } catch (e) {
            onShowToast && onShowToast(e.message || 'Ошибка');
        }
    };

    const createRole = async () => {
        const name = createName.trim();
        if (!name) {
            onShowToast && onShowToast('Введите название роли');
            return;
        }
        try {
            setCreating(true);
            const r = await fetch(`${API_BASE_URL}/api/roles`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ display_name: name, permissions: {} }),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(data.detail || 'Ошибка создания');
            setCreateName('');
            onShowToast && onShowToast('Роль создана');
            await load();
        } catch (e) {
            onShowToast && onShowToast(e.message || 'Ошибка');
        } finally {
            setCreating(false);
        }
    };

    const deleteRole = async (row) => {
        if (row.is_system || row.name === 'admin') {
            onShowToast && onShowToast('Эту роль нельзя удалить');
            return;
        }
        if (!window.confirm(`Удалить роль «${row.display_name || row.name}»?`)) return;
        try {
            const r = await fetch(`${API_BASE_URL}/api/roles/${row.id}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(data.detail || 'Ошибка удаления');
            onShowToast && onShowToast('Роль удалена');
            await load();
        } catch (e) {
            onShowToast && onShowToast(e.message || 'Ошибка');
        }
    };

    return (
        <div className="space-y-6">
            <h2 className="text-2xl font-bold text-slate-800">Роли и доступы</h2>
            <p className="text-sm text-slate-600 max-w-3xl">
                Для каждого раздела админки задайте права: просмотр, создание, редактирование, удаление.
                Роль «Администратор» — системная: полный доступ, удаление запрещено.
            </p>

            <div className="bg-white rounded-xl border border-slate-100 p-4 flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Новая роль (название)</label>
                    <input
                        value={createName}
                        onChange={(e) => setCreateName(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-slate-200"
                        placeholder="Например, Поддержка"
                    />
                </div>
                <button
                    type="button"
                    onClick={createRole}
                    disabled={creating}
                    className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-medium disabled:opacity-50"
                >
                    {creating ? 'Создание...' : 'Создать роль'}
                </button>
            </div>

            {loading ? (
                <div className="text-slate-500">Загрузка...</div>
            ) : (
                <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
                    <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 border-b">
                            <tr>
                                <th className="text-left px-4 py-3">Роль</th>
                                <th className="text-left px-4 py-3">Имя (slug)</th>
                                <th className="text-left px-4 py-3">Тип</th>
                                <th className="text-right px-4 py-3">Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((row) => (
                                <tr key={row.id} className="border-b border-slate-100">
                                    <td className="px-4 py-3 font-medium">{row.display_name || row.name}</td>
                                    <td className="px-4 py-3 font-mono text-slate-600">{row.name}</td>
                                    <td className="px-4 py-3">
                                        {row.is_system ? (
                                            <span className="text-xs bg-amber-100 text-amber-900 px-2 py-0.5 rounded">Системная</span>
                                        ) : (
                                            <span className="text-xs text-slate-500">Пользовательская</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right space-x-2">
                                        <button
                                            type="button"
                                            onClick={() => openEdit(row)}
                                            className="text-indigo-600 hover:underline"
                                        >
                                            Права
                                        </button>
                                        {!row.is_system && row.name !== 'admin' && (
                                            <button
                                                type="button"
                                                onClick={() => deleteRole(row)}
                                                className="text-red-600 hover:underline"
                                            >
                                                Удалить
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {editing && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
                    <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="px-4 py-3 border-b flex justify-between items-center">
                            <h3 className="font-semibold text-lg">
                                Права: {editing.display_name || editing.name}
                            </h3>
                            <button type="button" className="text-slate-500 hover:text-slate-800" onClick={() => setEditing(null)}>
                                ✕
                            </button>
                        </div>
                        <div className="overflow-auto p-4">
                            <table className="min-w-full text-xs border-collapse">
                                <thead>
                                    <tr className="border-b">
                                        <th className="text-left py-2 pr-4">Раздел</th>
                                        {ACTIONS.map((a) => (
                                            <th key={a.key} className="text-center px-1 py-2 w-24">
                                                {a.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sections.map((sec) => (
                                        <tr key={sec.key} className="border-b border-slate-100">
                                            <td className="py-2 pr-4 text-slate-800">{sec.label}</td>
                                            {ACTIONS.map((a) => (
                                                <td key={a.key} className="text-center py-1">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!(draftPerms[sec.key] || {})[a.key]}
                                                        onChange={() => togglePerm(sec.key, a.key)}
                                                        className="rounded border-slate-300"
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="px-4 py-3 border-t flex justify-end gap-2">
                            <button
                                type="button"
                                className="px-4 py-2 rounded-lg border border-slate-200"
                                onClick={() => setEditing(null)}
                            >
                                Отмена
                            </button>
                            <button
                                type="button"
                                className="px-4 py-2 rounded-lg bg-indigo-600 text-white"
                                onClick={saveEdit}
                            >
                                Сохранить
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
                Назначение роли пользователям — в разделе «Пользователи админки».
            </div>
        </div>
    );
};

export default RolesPage;
